import React from "react";
import { motion } from "framer-motion";
import { ShieldAlert, AlarmClock, PhoneCall, Users, AlertTriangle } from "lucide-react";

const emergencyPoints = [
  {
    icon: <ShieldAlert className="w-6 h-6 text-white" />,
    title: "24/7 Emergency Dispatch",
    description:
      "Our dedicated command center operates around the clock, ensuring rapid response to all emergency calls and onsite incidents.",
  },
  {
    icon: <AlarmClock className="w-6 h-6 text-white" />,
    title: "Immediate Response Protocols",
    description:
      "New Way Security personnel are trained in pre-defined emergency protocols to ensure swift and effective action.",
  },
  {
    icon: <PhoneCall className="w-6 h-6 text-white" />,
    title: "Real-Time Communication",
    description:
      "Guards are equipped with direct lines to supervisors and law enforcement, ensuring seamless communication in critical situations.",
  },
  {
    icon: <Users className="w-6 h-6 text-white" />,
    title: "Crowd & Incident Control",
    description:
      "Our team excels at managing crowd movement, calming public unrest, and maintaining order during emergencies.",
  },
  {
    icon: <AlertTriangle className="w-6 h-6 text-white" />,
    title: "Custom Emergency Plans",
    description:
      "We collaborate with clients to create customized emergency response strategies tailored to their site’s needs.",
  },
];

const Emergencies = () => {
  return (
    <div className="emergencies-page">
      <section className="bg-red-800 text-white py-20 text-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-4">Emergency Response</h1>
          <p className="text-lg md:text-xl">
            How New Way Security keeps clients safe in urgent situations.
          </p>
        </motion.div>
      </section>

      <section className="py-20 px-6 md:px-20 text-gray-800">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-red-800 mb-4">Prepared. Trained. Ready.</h2>
            <p className="text-lg text-gray-700">
              Emergencies demand clarity, speed, and confidence. New Way Security delivers all three—on demand.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8">
            {emergencyPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition border-l-4 border-red-800"
              >
                <div className="bg-red-800 p-2 rounded-full inline-flex mb-4">
                  {point.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-red-800">{point.title}</h3>
                <p className="text-gray-700 text-lg">{point.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-red-800 text-white py-16 px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">Need Emergency Security Support?</h2>
        <p className="mb-6 text-lg">
          Contact our 24/7 dispatch line now to activate New Way Security’s emergency response team.
        </p>
        <a
          href="tel:+14372863002"
          className="inline-block bg-white text-red-800 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition"
        >
          GET IMMEDIATE HELP
        </a>
      </section>
    </div>
  );
};

export default Emergencies;
